import { useShortcutStore } from "@/stores/shortcut-store";
import type { ReactNode } from "react";
import {
  Button,
  Icons,
  ShortcutKeyCaps,
  StatusDot,
  useMenuSurface,
} from "@/components/ui";
import { shortcutKeys, spokenShortcut } from "@/lib/shortcuts";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { trayMessages } from "@/i18n/messages/tray";
import "@/components/ui/control-tooltip-motion.css";

export function TrayTooltip({
  id,
  label,
  shortcut,
  placement = "below",
}: {
  id: string;
  label: string;
  shortcut?: string;
  placement?: "above" | "below" | "left" | "right";
}) {
  useShortcutStore((state) => state.overrides);
  const card = useMenuSurface() === "card";
  const spoken = shortcut ? spokenShortcut(shortcut) : "";
  // Windows spells a shortcut out as one keycap, which would repeat the text.
  const keys = shortcut
    ? shortcutKeys(shortcut).filter((key) => key !== spoken)
    : [];
  return (
    <span
      id={id}
      role="tooltip"
      className={cn(
        "denker-control-tooltip pointer-events-none absolute z-20 w-max max-w-[240px] px-2 py-1 text-canvas-sm",
        // On the wall it wears the card, as the wall's menus do.
        card
          ? "denker-card-tooltip rounded-preview"
          : "denker-glass-tooltip rounded-control border text-primary",
        placement === "below"
          ? "right-0 top-full mt-2"
          : placement === "left"
            ? "right-full top-1/2 mr-2 -translate-y-1/2"
            : placement === "right"
              ? "left-full top-1/2 ml-2 -translate-y-1/2"
              : "bottom-full left-1/2 mb-2 -translate-x-1/2",
      )}
    >
      {shortcut ? (
        <span className="flex flex-col items-start gap-0.5">
          <span>{label}</span>
          <span className="flex items-center gap-2 text-secondary">
            <span>{spoken}</span>
            {keys.length > 0 && (
              <ShortcutKeyCaps keys={keys} size="inline" aria-hidden="true" />
            )}
          </span>
        </span>
      ) : (
        label
      )}
    </span>
  );
}

export function TrayIconButton({
  label,
  testId,
  active,
  shortcut,
  disabled,
  attention,
  tooltipPlacement = "above",
  onClick,
  children,
}: {
  label: string;
  testId: string;
  active?: boolean;
  shortcut?: string;
  disabled?: boolean;
  attention?: boolean;
  tooltipPlacement?: "above" | "below" | "left";
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <span className="group relative">
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        className={cn("text-muted", active && "text-accent")}
        aria-label={label}
        aria-describedby={`${testId}-tooltip`}
        aria-pressed={active}
        disabled={disabled}
        data-testid={testId}
        onClick={onClick}
      >
        {children}
      </Button>
      {attention ? (
        <StatusDot
          tone="unread"
          className="pointer-events-none absolute right-0.5 top-0.5"
        />
      ) : null}
      <TrayTooltip
        id={`${testId}-tooltip`}
        label={label}
        shortcut={shortcut}
        placement={tooltipPlacement}
      />
    </span>
  );
}

export function TrayNavigationButtons({
  onSearch,
  onShortcuts,
}: {
  onSearch: () => void;
  onShortcuts: () => void;
}) {
  const t = useT(trayMessages);
  return (
    <div className="flex items-center gap-1">
      <TrayIconButton
        label={t("searchChats")}
        testId="desktop-tray-search"
        onClick={onSearch}
      >
        <Icons.Search className="h-4 w-4" />
      </TrayIconButton>
      <TrayIconButton
        label={t("keyboardShortcuts")}
        testId="desktop-tray-shortcuts"
        onClick={onShortcuts}
      >
        <Icons.Info className="h-4 w-4" aria-hidden="true" />
      </TrayIconButton>
    </div>
  );
}
